import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { AdminService, Relative, HierarchyData } from '../../services/admin.service';
import { AuthService } from '../../services/auth.service';
import { ChatPopupComponent } from '../chat-popup/chat-popup.component';

@Component({
  selector: 'app-hierarchy',
  standalone: true,
  imports: [CommonModule, ChatPopupComponent],
  templateUrl: './hierarchy.component.html',
  styleUrl: './hierarchy.component.css'
})
export class HierarchyComponent implements OnInit {
  hierarchyData: HierarchyData | null = null;
  isLoading = true;
  relativeId: number | null = null;
  
  // Siblings pagination properties
  currentSiblingPage = 0;
  siblingsPerPage = 2;
  visibleSiblings: Relative[] = [];

  // Chat properties
  showChatPopup = false;
  chatOtherUserId = 0;
  chatOtherUserName = '';
  chatOtherUserProfileImageUrl?: string;

  // Selection properties
  selectedRelativeId: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private adminService: AdminService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const idParam = params['id'];
      if (idParam) {
        // ID provided in URL - use it
        this.relativeId = +idParam;
        this.loadHierarchyData();
      } else {
        // No ID provided - redirect to login (this shouldn't happen with the new routing)
        console.error('No ID provided in hierarchy route');
        this.router.navigate(['/login']);
      }
    });
  }

  loadHierarchyData(): void {
    if (!this.relativeId) return;
    
    this.isLoading = true;
    console.log('🔍 Loading hierarchy data for relative ID:', this.relativeId);
    console.log('🔍 Current user is admin:', this.authService.isAdmin());
    
    // Use different API endpoints based on user role and whether viewing own hierarchy
    const currentUser = this.authService.getCurrentUser();
    const isViewingOwnHierarchy = currentUser && currentUser.id === this.relativeId;
    
    let hierarchyObservable;
    
    // Use admin service for all hierarchy requests to ensure consistency
    console.log('🔍 Using admin service for hierarchy request');
    hierarchyObservable = this.adminService.getRelativeHierarchy(this.relativeId);
    
    hierarchyObservable.subscribe({
      next: (data:any) => {
        console.log('✅ Hierarchy data loaded:', data);
        this.hierarchyData = data.data || data; // Handle different response structures
        console.log('🔍 Processed hierarchy data:', this.hierarchyData);
        console.log('🔍 Spouse data:', this.hierarchyData?.spouse);
        console.log('🔍 Current person spouse referral code:', this.hierarchyData?.current?.spouseReferralCode);
        console.log('🔍 All hierarchy data keys:', Object.keys(this.hierarchyData || {}));
        this.updateVisibleSiblings(); // Initialize visible siblings
        // Auto-select the current person (self) when loading their hierarchy
        if (this.relativeId) {
          this.selectedRelativeId = this.relativeId;
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('❌ Error loading hierarchy data:', error);
        // Fallback to mock data if API fails
        this.mockHierarchyData();
      }
    });
  }

  mockHierarchyData(): void {
    // Mock data structure - replace with actual API calls
    console.log('🔍 Using mock hierarchy data for testing');
    this.hierarchyData = {
      parents: [
        { id: 1, firstName: 'John', lastName: 'Doe', email: 'john@example.com', mobileNumber: '1234567890', gender: 'Male', dateOfBirth: '1950-01-01', isDeceased: true, isActive: true, relationshipType: 'Father', parentReferralCode: undefined, qualification: 'Engineer', profession: 'Software Engineer', referralCode: 'REF001', username: 'john.doe', isApproved: true, createdAt: '2023-01-01' },
        { id: 2, firstName: 'Jane', lastName: 'Doe', email: 'jane@example.com', mobileNumber: '1234567891', gender: 'Female', dateOfBirth: '1952-01-01', isDeceased: false, isActive: true, relationshipType: 'Mother', parentReferralCode: undefined, qualification: 'Teacher', profession: 'Educator', referralCode: 'REF002', username: 'jane.doe', isApproved: true, createdAt: '2023-01-01' }
      ],
      siblings: [
        { id: 3, firstName: 'Bob', lastName: 'Doe', email: 'bob@example.com', mobileNumber: '1234567892', gender: 'Male', dateOfBirth: '1980-01-01', isDeceased: true, isActive: true, relationshipType: 'Brother', parentReferralCode: undefined, qualification: 'Doctor', profession: 'Physician', referralCode: 'REF003', username: 'bob.doe', isApproved: true, createdAt: '2023-01-01' },
        { id: 4, firstName: 'Alice', lastName: 'Doe', email: 'alice@example.com', mobileNumber: '1234567893', gender: 'Female', dateOfBirth: '1982-01-01', isDeceased: false, isActive: true, relationshipType: 'Sister', parentReferralCode: undefined, qualification: 'Lawyer', profession: 'Attorney', referralCode: 'REF004', username: 'alice.doe', isApproved: true, createdAt: '2023-01-01' },
        { id: 8, firstName: 'Charlie', lastName: 'Doe', email: 'charlie@example.com', mobileNumber: '1234567898', gender: 'Male', dateOfBirth: '1984-01-01', isDeceased: false, isActive: true, relationshipType: 'Brother', parentReferralCode: undefined, qualification: 'Engineer', profession: 'Software Engineer', referralCode: 'REF009', username: 'charlie.doe', isApproved: true, createdAt: '2023-01-01' },
        { id: 9, firstName: 'Diana', lastName: 'Doe', email: 'diana@example.com', mobileNumber: '1234567899', gender: 'Female', dateOfBirth: '1986-01-01', isDeceased: false, isActive: true, relationshipType: 'Sister', parentReferralCode: undefined, qualification: 'Teacher', profession: 'Educator', referralCode: 'REF010', username: 'diana.doe', isApproved: true, createdAt: '2023-01-01' },
        { id: 10, firstName: 'Edward', lastName: 'Doe', email: 'edward@example.com', mobileNumber: '1234567900', gender: 'Male', dateOfBirth: '1988-01-01', isDeceased: false, isActive: true, relationshipType: 'Brother', parentReferralCode: undefined, qualification: 'Artist', profession: 'Graphic Designer', referralCode: 'REF011', username: 'edward.doe', isApproved: true, createdAt: '2023-01-01' },
        { id: 11, firstName: 'Fiona', lastName: 'Doe', email: 'fiona@example.com', mobileNumber: '1234567901', gender: 'Female', dateOfBirth: '1990-01-01', isDeceased: false, isActive: true, relationshipType: 'Sister', parentReferralCode: undefined, qualification: 'Nurse', profession: 'Healthcare Worker', referralCode: 'REF012', username: 'fiona.doe', isApproved: true, createdAt: '2023-01-01' }
      ],
      spouse: { id: 5, firstName: 'Sarah', lastName: 'Smith', email: 'sarah@example.com', mobileNumber: '1234567894', gender: 'Female', dateOfBirth: '1985-01-01', isDeceased: false, isActive: true, relationshipType: 'Spouse', parentReferralCode: undefined, qualification: 'Designer', profession: 'UI/UX Designer', referralCode: 'REF005', username: 'sarah.smith', isApproved: true, createdAt: '2023-01-01' },
      children: [
        { id: 6, firstName: 'Tom', lastName: 'Doe', email: 'tom@example.com', mobileNumber: '1234567895', gender: 'Male', dateOfBirth: '2010-01-01', isDeceased: false, isActive: true, relationshipType: 'Son', parentReferralCode: undefined, qualification: 'Student', profession: 'Student', referralCode: 'REF006', username: 'tom.doe', isApproved: true, createdAt: '2023-01-01' },
        { id: 7, firstName: 'Emma', lastName: 'Doe', email: 'emma@example.com', mobileNumber: '1234567896', gender: 'Female', dateOfBirth: '2012-01-01', isDeceased: false, isActive: true, relationshipType: 'Daughter', parentReferralCode: undefined, qualification: 'Student', profession: 'Student', referralCode: 'REF007', username: 'emma.doe', isApproved: true, createdAt: '2023-01-01' }
      ],
      current: { id: this.relativeId!, firstName: 'Current', lastName: 'Person', email: 'current@example.com', mobileNumber: '1234567897', gender: 'Male', dateOfBirth: '1985-01-01', isDeceased: false, isActive: true, relationshipType: 'Self', parentReferralCode: undefined, qualification: 'Engineer', profession: 'Software Engineer', referralCode: 'REF008', username: 'current.person', isApproved: true, createdAt: '2023-01-01' }
    };
    this.updateVisibleSiblings(); // Initialize visible siblings for mock data
    this.isLoading = false;
  }

  viewRelative(relative: Relative): void {
    console.log('🔍 Viewing relative:', relative);
    this.router.navigate(['/relative', relative.id]);
  }

  goBack(): void {
    // Use browser history if available, otherwise navigate to manage-relatives
    if (window.history.length > 1) {
      this.location.back();
    } else {
      // Fallback to default navigation
      const userId = this.authService.getCurrentUserId();
      if (userId) {
        // If user came from their own hierarchy (My Network), go to home
        if (this.relativeId === userId && !this.authService.isAdmin()) {
          this.router.navigate(['/home']);
        } else {
          // Otherwise go to manage-relatives (for admin) or home (for relative)
          this.router.navigate([this.authService.isAdmin() ? '/manage-relatives' : '/home']);
        }
      } else {
        this.router.navigate(['/manage-relatives']);
      }
    }
  }

  getInitials(relative: Relative): string {
    return `${relative.firstName.charAt(0)}${relative.lastName.charAt(0)}`.toUpperCase();
  }

  calculateAge(dateOfBirth: string): number {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  }

  getRelationshipType(relative: Relative, context: 'parent' | 'sibling' | 'spouse' | 'child'): string {
    // Define valid relationship types for each context
    const validTypes = {
      'parent': ['Father', 'Mother'],
      'sibling': ['Brother', 'Sister'],
      'spouse': ['Spouse'],
      'child': ['Son', 'Daughter', 'Child-Spouse']
    };
    
    // Only use API relationshipType if it's valid for the context
    if (relative.relationshipType && validTypes[context].includes(relative.relationshipType)) {
      return relative.relationshipType;
    }
    
    // Fallback based on context and gender
    switch (context) {
      case 'parent':
        return relative.gender === 'Male' ? 'Father' : 'Mother';
      case 'sibling':
        return relative.gender === 'Male' ? 'Brother' : 'Sister';
      case 'spouse':
        return 'Spouse';
      case 'child':
        // Handle Child-Spouse relationship type
        if (relative.relationshipType === 'Child-Spouse') {
          return relative.gender === 'Male' ? 'Son & Spouse' : 'Daughter & Spouse';
        }
        return relative.gender === 'Male' ? 'Son' : 'Daughter';
      default:
        return 'Relative';
    }
  }

  /**
   * Get parent by gender (Male/Female)
   */
  getParentByGender(gender: 'Male' | 'Female'): Relative | null {
    if (!this.hierarchyData?.parents) {
      return null;
    }
    return this.hierarchyData.parents.find(parent => parent.gender === gender) || null;
  }

  /**
   * View parent couple details
   */
  viewParentCouple(): void {
    // For now, show the first parent's details
    // In the future, this could show a combined view or modal
    if (this.hierarchyData?.parents && this.hierarchyData.parents.length > 0) {
      this.viewRelative(this.hierarchyData.parents[0]);
    }
  }

  /**
   * Select a relative as the current person and navigate to their hierarchy
   */
  selectAsCurrent(relative: Relative): void {
    console.log('🔍 Selecting as current:', relative);
    this.router.navigate(['/hierarchy', relative.id]);
  }

  /**
   * Add a relative with parent referral code pre-filled
   */
  addRelativeWithParent(parent: Relative): void {
    console.log('🔍 Adding relative with parent:', parent);
    this.router.navigate(['/signup'], { 
      queryParams: { 
        parentReferralCode: parent.referralCode,
        parentName: `${parent.firstName} ${parent.lastName}`
      } 
    });
  }

  /**
   * Navigate to previous page of siblings
   */
  previousSiblings(): void {
    if (this.currentSiblingPage > 0) {
      this.currentSiblingPage--;
      this.updateVisibleSiblings();
    }
  }

  /**
   * Navigate to next page of siblings
   */
  nextSiblings(): void {
    const totalPages = Math.ceil((this.hierarchyData?.siblings.length || 0) / this.siblingsPerPage);
    if (this.currentSiblingPage < totalPages - 1) {
      this.currentSiblingPage++;
      this.updateVisibleSiblings();
    }
  }

  /**
   * Update the visible siblings based on current page
   */
  updateVisibleSiblings(): void {
    if (!this.hierarchyData?.siblings) {
      this.visibleSiblings = [];
      return;
    }

    const startIndex = this.currentSiblingPage * this.siblingsPerPage;
    const endIndex = startIndex + this.siblingsPerPage;
    this.visibleSiblings = this.hierarchyData.siblings.slice(startIndex, endIndex);
  }

  /**
   * Check if previous siblings button should be enabled
   */
  canGoToPreviousSiblings(): boolean {
    return this.currentSiblingPage > 0;
  }

  /**
   * Check if next siblings button should be enabled
   */
  canGoToNextSiblings(): boolean {
    const totalPages = Math.ceil((this.hierarchyData?.siblings.length || 0) / this.siblingsPerPage);
    return this.currentSiblingPage < totalPages - 1;
  }

  /**
   * Open chat with a relative
   */
  openChat(relative: Relative): void {
    this.chatOtherUserId = relative.id;
    this.chatOtherUserName = `${relative.firstName} ${relative.lastName}`.trim();
    this.chatOtherUserProfileImageUrl = relative.profileImageUrl;
    this.showChatPopup = true;
  }

  /**
   * Close chat popup
   */
  closeChat(): void {
    this.showChatPopup = false;
    this.chatOtherUserId = 0;
    this.chatOtherUserName = '';
    this.chatOtherUserProfileImageUrl = undefined;
  }

  /**
   * Select a relative card
   */
  selectRelative(relative: Relative): void {
    this.selectedRelativeId = relative.id;
    console.log('🔍 Selected relative:', relative);
  }

  /**
   * Check if a relative is selected
   */
  isSelected(relative: Relative): boolean {
    return this.selectedRelativeId === relative.id;
  }

  /**
   * Select parent couple
   */
  selectParentCouple(): void {
    if (this.hierarchyData && this.hierarchyData.parents.length > 0) {
      // Select the first parent as representative of the couple
      this.selectedRelativeId = this.hierarchyData.parents[0].id;
      console.log('🔍 Selected parent couple');
    }
  }

  /**
   * Check if parent couple is selected
   */
  isParentSelected(): boolean {
    if (!this.hierarchyData || this.hierarchyData.parents.length === 0) {
      return false;
    }
    return this.hierarchyData.parents.some(parent => this.selectedRelativeId === parent.id);
  }
}
